import { AxiosInstance, InternalAxiosRequestConfig, AxiosResponse } from 'axios';
import { DemoService } from './demo-service';
import {
    getDemoUserData,
    getDemoNetwork,
    getDemoReportData,
    getDemoProductCodes,
    getDemoMetricSettings,
    getDemoProductReportData,
    getDemoCompanyReportData,
    getDemoPlacesData,
    getDemoMetricProductData
} from './demo-data';

/**
 * Setup demo mode interceptor for API calls
 */
export function setupDemoInterceptor(api: AxiosInstance): void {
    const demoService = DemoService.getInstance();

    // Request interceptor - block real API calls in demo mode
    api.interceptors.request.use(
        (config) => {
            // If in demo mode, reject the request to trigger the response error handler
            if (demoService.isDemoMode()) {
                const url = config.url || '';
                console.log(`🎭 Demo mode - blocking real API request: ${config.method?.toUpperCase()} ${url}`);

                // Reject with a custom error to trigger mock data response
                return Promise.reject({
                    config,
                    isDemo: true,
                    message: 'Demo mode - using mock data'
                });
            }
            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    // Response interceptor to return mock data in demo mode
    api.interceptors.response.use(
        (response) => response,
        async (error) => {
            // Only intercept if in demo mode
            if (!demoService.isDemoMode()) {
                return Promise.reject(error);
            }

            const config = error.config as InternalAxiosRequestConfig;
            const url = config.url || '';
            const method = config.method?.toUpperCase() || 'GET';

            console.log(`🎭 Demo mode - returning mock data for ${method} ${url}`);

            // For POST/PUT/DELETE requests, return a success response
            if (method === 'POST' || method === 'PUT' || method === 'DELETE') {
                const mockResponse: AxiosResponse = {
                    data: {
                        success: true,
                        message: 'Demo mode - changes not persisted',
                        ...getMockDataForUrl(url)
                    },
                    status: 200,
                    statusText: 'OK',
                    headers: {},
                    config: config
                };
                return Promise.resolve(mockResponse);
            }

            // Mock response based on URL pattern for GET requests
            const mockResponse: AxiosResponse = {
                data: getMockDataForUrl(url),
                status: 200,
                statusText: 'OK',
                headers: {},
                config: config
            };

            return Promise.resolve(mockResponse);
        }
    );
}

/**
 * Get mock data based on URL pattern
 */
function getMockDataForUrl(url: string): any {
    // Check more specific patterns first before general ones

    // Product codes endpoint (check before /network)
    if (url.includes('/product_codes') || url.includes('/products') || url.includes('/product-codes')) {
        return getDemoProductCodes();
    }

    // Network endpoint with report parameters (check before general /network)
    if (url.includes('/network') && (url.includes('year=') || url.includes('month=') || url.includes('report='))) {
        // Extract year and month from URL
        const urlParams = new URLSearchParams(url.split('?')[1] || '');
        const year = urlParams.get('year');
        const month = urlParams.get('month');
        const requestedPeriod = year && month ? `${year}-${month}` : null;
        const reportType = urlParams.get('report');

        // Check for metric_product view (time-series format)
        if (reportType === 'metric_product') {
            console.log('🎭 Demo mode - returning metric_product data:', url);
            return getDemoMetricProductData();
        }

        // Check if this is a places query
        if (url.includes('places=1') || url.includes('places=true')) {
            console.log('🎭 Demo mode - returning places data for network query:', url, 'period:', requestedPeriod);
            const allData = getDemoPlacesData();

            // Filter data for the requested period only
            if (requestedPeriod) {
                const filteredData = allData.data.filter(item => item.period === requestedPeriod);
                return {
                    ...allData,
                    data: filteredData
                };
            }

            return allData;
        }

        console.log('🎭 Demo mode - returning report data for network query:', url, 'period:', requestedPeriod);
        const allData = getDemoReportData();

        // Filter data for the requested period only
        if (requestedPeriod) {
            const filteredData = allData.data.filter(item => item.period === requestedPeriod);
            return {
                ...allData,
                data: filteredData
            };
        }

        return allData;
    }

    // Reports endpoint - check for specific patterns
    if (url.includes('/reports')) {
        // Company-specific report
        const companyMatch = url.match(/\/reports\/company\/(\d+)/);
        if (companyMatch) {
            const companyId = parseInt(companyMatch[1], 10);
            return getDemoCompanyReportData(companyId);
        }

        // Product-specific report
        const productMatch = url.match(/\/reports\/product\/([^/]+)/);
        if (productMatch) {
            const productCode = productMatch[1];
            return getDemoProductReportData(productCode);
        }

        // General reports
        return getDemoReportData();
    }

    // Metric settings endpoint (check before /settings)
    if (url.includes('/settings/metrics') || url.includes('/metric-settings')) {
        return getDemoMetricSettings();
    }

    // Saved views (dashboards, report pages): demo has none, and a write is not
    // persisted. Checked before the generic matches below.
    if (url.includes('/views/default')) {
        return { view_type: null, view_id: null };
    }
    if (url.includes('/views')) {
        return { total: 0, page: 1, page_size: 0, settings: {}, data: [] };
    }

    // User endpoint
    if (url.includes('/user')) {
        return getDemoUserData();
    }

    // Network endpoint (general - for network structure)
    if (url.includes('/network')) {
        return getDemoNetwork();
    }

    // Stats or analytics endpoints
    if (url.includes('/stats') || url.includes('/analytics')) {
        return getDemoReportData();
    }

    // Settings endpoint (general)
    if (url.includes('/settings')) {
        return getDemoMetricSettings();
    }

    // Default empty response
    console.warn('🎭 Demo mode - no mock data for URL:', url);
    return {};
}